import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Copy,
  Download,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  Printer,
  Shield,
  Smartphone,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import client from "../../src/api/client";
import PasswordRequirements from "./PasswordRequirements";
import SudoModeModal from "./SudoModeModal";

const buildSecurityProfileDefaults = () => ({
  signInMethods: {
    email: "",
    passwordLastChangedAt: null,
  },
  twoFactorAuthentication: {
    authenticatorAppEnabled: false,
    smsEnabled: false,
  },
  recoveryOptions: {
    recoveryCodes: {
      total: 0,
      remaining: 0,
      exhausted: false,
      generatedAt: null,
      stale: true,
      canRevealExistingCodes: false,
    },
  },
});

const normalizeSecurityProfile = (payload = {}) => {
  const defaults = buildSecurityProfileDefaults();
  const signInMethods = payload?.signInMethods ?? defaults.signInMethods;
  const twoFactor = payload?.twoFactorAuthentication ?? defaults.twoFactorAuthentication;
  const recoveryCodes = payload?.recoveryOptions?.recoveryCodes ?? defaults.recoveryOptions.recoveryCodes;

  return {
    signInMethods: {
      email: String(signInMethods.email ?? defaults.signInMethods.email),
      passwordLastChangedAt: signInMethods.passwordLastChangedAt ?? defaults.signInMethods.passwordLastChangedAt,
    },
    twoFactorAuthentication: {
      authenticatorAppEnabled: Boolean(twoFactor.authenticatorAppEnabled),
      smsEnabled: Boolean(twoFactor.smsEnabled),
    },
    recoveryOptions: {
      recoveryCodes: {
        total: Number(recoveryCodes.total ?? 0),
        remaining: Number(recoveryCodes.remaining ?? 0),
        exhausted: Boolean(recoveryCodes.exhausted),
        generatedAt: recoveryCodes.generatedAt ?? null,
        stale: recoveryCodes.stale !== undefined ? Boolean(recoveryCodes.stale) : true,
        canRevealExistingCodes: Boolean(recoveryCodes.canRevealExistingCodes),
      },
    },
  };
};

const validatePassword = (password) => {
  if (!password || password.length < 8) {
    return "Password must be at least 8 characters long.";
  }
  if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one uppercase letter.";
  }
  if (!/[a-z]/.test(password)) {
    return "Password must contain at least one lowercase letter.";
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    return "Password must contain at least one special character.";
  }
  return null;
};

const formatDateTime = (value) => {
  if (!value) {
    return "Never";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }
  return parsed.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

const clearAdminAuthSession = () => {
  window.localStorage.removeItem("adminToken");
  window.localStorage.removeItem("adminUser");
  delete client.defaults.headers.common.Authorization;
};

export default function PasswordAuthenticationSettings() {
  const navigate = useNavigate();

  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [profile, setProfile] = useState(buildSecurityProfileDefaults);

  const [view, setView] = useState("overview");
  const [sudoModalOpen, setSudoModalOpen] = useState(false);
  const [sudoToken, setSudoToken] = useState("");
  const [pendingSensitiveTarget, setPendingSensitiveTarget] = useState(null);

  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);
  const [recoveryMessage, setRecoveryMessage] = useState(null);
  const [authenticatorSetupOpen, setAuthenticatorSetupOpen] = useState(false);
  const [authenticatorLoading, setAuthenticatorLoading] = useState(false);
  const [authenticatorSaving, setAuthenticatorSaving] = useState(false);
  const [authenticatorSetupToken, setAuthenticatorSetupToken] = useState("");
  const [authenticatorQrCodeDataUrl, setAuthenticatorQrCodeDataUrl] = useState("");
  const [authenticatorManualKey, setAuthenticatorManualKey] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [authenticatorMessage, setAuthenticatorMessage] = useState(null);
  const [authenticatorError, setAuthenticatorError] = useState(null);

  const [passwordStep, setPasswordStep] = useState("form");
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [passwordVerifyToken, setPasswordVerifyToken] = useState("");
  const [passwordOtpCode, setPasswordOtpCode] = useState("");
  const [passwordUseBackupCode, setPasswordUseBackupCode] = useState(false);
  const [passwordOtpModalOpen, setPasswordOtpModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);

  const requirementsValue = useMemo(() => {
    if (confirmFocused && passwordForm.confirmNewPassword) {
      return passwordForm.confirmNewPassword;
    }
    return passwordForm.newPassword || "";
  }, [confirmFocused, passwordForm.confirmNewPassword, passwordForm.newPassword]);

  const shouldShowPasswordRequirements = useMemo(
    () => Boolean(passwordFocused || confirmFocused || passwordForm.newPassword || passwordForm.confirmNewPassword),
    [confirmFocused, passwordFocused, passwordForm.confirmNewPassword, passwordForm.newPassword],
  );

  const loadSecurityProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      setProfileError(null);
      const response = await client.get("/api/admin/auth/security/profile");
      setProfile(normalizeSecurityProfile(response.data ?? {}));
    } catch (err) {
      setProfileError(err?.response?.data?.message ?? "Unable to load authentication settings.");
      setProfile(buildSecurityProfileDefaults());
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSecurityProfile();
  }, [loadSecurityProfile]);

  const openSensitiveView = (target) => {
    setPendingSensitiveTarget(target);
    setSudoModalOpen(true);
    setRecoveryError(null);
  };

  const initializeAuthenticatorSetup = async (tokenOverride) => {
    const activeSudoToken = tokenOverride || sudoToken;
    if (!activeSudoToken) {
      openSensitiveView("authenticator");
      return;
    }

    try {
      setAuthenticatorLoading(true);
      setAuthenticatorError(null);
      setAuthenticatorMessage(null);

      const response = await client.post(
        "/api/admin/auth/security/authenticator/setup/init",
        {},
        {
          headers: {
            "X-Sudo-Token": activeSudoToken,
          },
        },
      );

      setAuthenticatorSetupOpen(true);
      setAuthenticatorSetupToken(String(response?.data?.setupToken ?? ""));
      setAuthenticatorQrCodeDataUrl(String(response?.data?.qrCodeDataUrl ?? ""));
      setAuthenticatorManualKey(String(response?.data?.manualKey ?? ""));
      setAuthenticatorCode("");
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.requiresSudo) {
        setSudoToken("");
        openSensitiveView("authenticator");
        return;
      }
      setAuthenticatorError(err?.response?.data?.message ?? "Unable to initialize authenticator setup.");
    } finally {
      setAuthenticatorLoading(false);
    }
  };

  const handleSudoSuccess = (token) => {
    setSudoToken(token);
    setSudoModalOpen(false);
    if (pendingSensitiveTarget === "authenticator") {
      setView("authenticator");
      if (profile.twoFactorAuthentication.authenticatorAppEnabled) {
        setAuthenticatorSetupOpen(false);
      } else {
        initializeAuthenticatorSetup(token);
      }
    }
    if (pendingSensitiveTarget === "recovery-codes") {
      setView("recovery-codes");
    }
    setPendingSensitiveTarget(null);
  };

  const handleSudoCancel = () => {
    setSudoModalOpen(false);
    setPendingSensitiveTarget(null);
  };

  const handleCopyRecoveryCodes = async () => {
    if (!recoveryCodes.length) {
      return;
    }
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      setRecoveryMessage("Recovery codes copied.");
    } catch {
      setRecoveryError("Unable to copy recovery codes.");
    }
  };

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes.length) {
      return;
    }
    const content = recoveryCodes.join("\r\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "recovery-codes.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handlePrintRecoveryCodes = () => {
    if (!recoveryCodes.length) {
      return;
    }
    const content = recoveryCodes.join("\n");
    const printWindow = window.open("", "_blank", "width=640,height=720");
    if (!printWindow) {
      setRecoveryError("Unable to open print window.");
      return;
    }
    printWindow.document.write(`<pre>${content}</pre>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleAuthenticatorSave = async (event) => {
    event.preventDefault();
    const code = authenticatorCode.trim();
    setAuthenticatorError(null);
    setAuthenticatorMessage(null);

    if (!/^\d{6}$/.test(code)) {
      setAuthenticatorError("Enter a valid 6-digit authenticator code.");
      return;
    }

    if (!authenticatorSetupToken) {
      setAuthenticatorError("Setup session expired. Please restart setup.");
      return;
    }

    try {
      setAuthenticatorSaving(true);
      const response = await client.post(
        "/api/admin/auth/security/authenticator/setup/verify",
        {
          setupToken: authenticatorSetupToken,
          otpCode: code,
        },
        {
          headers: {
            "X-Sudo-Token": sudoToken,
          },
        },
      );

      setAuthenticatorMessage(response?.data?.message ?? "Authenticator app configured successfully.");
      setAuthenticatorSetupOpen(false);
      setAuthenticatorCode("");
      setAuthenticatorSetupToken("");

      const nextCodes = Array.isArray(response?.data?.recoveryCodes) ? response.data.recoveryCodes : [];
      if (nextCodes.length) {
        setRecoveryCodes(nextCodes);
        setView("recovery-codes");
        setRecoveryMessage("New recovery codes generated. Store them securely now.");
      }
      await loadSecurityProfile();
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.requiresSudo) {
        setSudoToken("");
        openSensitiveView("authenticator");
        return;
      }
      setAuthenticatorError(err?.response?.data?.message ?? "Unable to verify authenticator setup.");
    } finally {
      setAuthenticatorSaving(false);
    }
  };

  const handleGenerateRecoveryCodes = async () => {
    if (!sudoToken) {
      openSensitiveView("recovery-codes");
      return;
    }

    try {
      setRecoveryLoading(true);
      setRecoveryError(null);
      setRecoveryMessage(null);

      const response = await client.post(
        "/api/admin/auth/security/recovery-codes/review",
        {},
        {
          headers: {
            "X-Sudo-Token": sudoToken,
          },
        },
      );

      const nextCodes = Array.isArray(response?.data?.codes) ? response.data.codes : [];
      setRecoveryCodes(nextCodes);
      setRecoveryMessage(response?.data?.message ?? "New recovery codes generated. Your previous recovery codes no longer work.");
      await loadSecurityProfile();
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.requiresSudo) {
        setSudoToken("");
        openSensitiveView("recovery-codes");
        return;
      }
      setRecoveryError(err?.response?.data?.message ?? "Unable to generate recovery codes.");
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordError(null);
    setPasswordMessage(null);
  };

  const handlePasswordStart = async (event) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    const oldPassword = passwordForm.oldPassword;
    const newPassword = passwordForm.newPassword;
    const confirmNewPassword = passwordForm.confirmNewPassword;

    if (!oldPassword) {
      setPasswordError("Old password is required.");
      return;
    }

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await client.post("/api/admin/auth/password/change/request", {
        oldPassword,
        newPassword,
        confirmNewPassword,
      });

      setPasswordVerifyToken(response?.data?.verificationToken ?? "");
      setPasswordStep("otp");
      setPasswordMessage("Password verified. Enter your OTP to finalize the password change.");
      setPasswordOtpModalOpen(true);
    } catch (err) {
      setPasswordError(err?.response?.data?.message ?? "Unable to start password change.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordFinish = async (event) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordMessage(null);

    const otpCode = passwordOtpCode.trim();
    if (!otpCode) {
      setPasswordError("OTP code is required.");
      return;
    }

    if (!passwordUseBackupCode && otpCode.length !== 6) {
      setPasswordError("Authenticator OTP must be 6 digits.");
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await client.post("/api/admin/auth/password/change/confirm", {
        verificationToken: passwordVerifyToken,
        otpCode,
        isBackupCode: passwordUseBackupCode,
      });

      setPasswordMessage(response?.data?.message ?? "Password updated successfully.");
      setPasswordError(null);
      setPasswordOtpModalOpen(false);

      window.setTimeout(() => {
        clearAdminAuthSession();
        navigate("/admin/login", { replace: true });
      }, 1200);
    } catch (err) {
      setPasswordError(err?.response?.data?.message ?? "Unable to finalize password change.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetPasswordFlow = () => {
    setPasswordStep("form");
    setPasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    });
    setPasswordVerifyToken("");
    setPasswordOtpCode("");
    setPasswordUseBackupCode(false);
    setPasswordOtpModalOpen(false);
    setPasswordError(null);
    setPasswordMessage(null);
    setPasswordLoading(false);
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordFocused(false);
    setConfirmFocused(false);
  };

  const renderOverview = () => (
    <div className="admin-card-grid cols-3 auth-settings-overview-grid">
      <article className="admin-card">
        <div className="admin-heading-inline">
          <Mail size={16} />
          <div>
            <h3 className="auth-settings-title">Sign-in Methods</h3>
            <p className="admin-muted">Manage your account credentials and password security.</p>
          </div>
        </div>
        <div className="admin-setting-list auth-settings-list">
          <div className="admin-setting-item">
            <div>
              <strong>Email</strong>
              <p className="admin-muted auth-settings-muted-reset">{profile.signInMethods.email || "Unavailable"}</p>
            </div>
            <span className="admin-chip is-blue">Primary Email</span>
          </div>
          <div className="admin-setting-item">
            <div>
              <strong>Password</strong>
              <p className="admin-muted auth-settings-muted-reset">
                Last credential update: {formatDateTime(profile.signInMethods.passwordLastChangedAt)}
              </p>
            </div>
            <button
              type="button"
              className="admin-button is-primary"
              onClick={() => {
                resetPasswordFlow();
                setView("change-password");
              }}
            >
              Change Password
            </button>
          </div>
        </div>
      </article>

      <article className="admin-card">
        <div className="admin-heading-inline">
          <Shield size={16} />
          <div>
            <h3 className="auth-settings-title">Two-factor Authentication</h3>
            <p className="admin-muted">Secure account access with a second verification factor.</p>
          </div>
        </div>
        <div className="admin-setting-list auth-settings-list">
          <div className="admin-setting-item">
            <div>
              <strong>Authenticator app</strong>
              <p className="admin-muted auth-settings-muted-reset">
                {profile.twoFactorAuthentication.authenticatorAppEnabled
                  ? "Enabled and required for sign-in."
                  : "Not configured."}
              </p>
            </div>
            <button
              type="button"
              className="admin-button"
              onClick={() => openSensitiveView("authenticator")}
            >
              Manage
            </button>
          </div>
          <div className="admin-setting-item">
            <div>
              <strong>SMS / Text message</strong>
              <p className="admin-muted auth-settings-muted-reset">UI placeholder only. Backend integration is not enabled yet.</p>
            </div>
            <button type="button" className="admin-button" disabled>
              Coming Soon
            </button>
          </div>
        </div>
      </article>

      <article className="admin-card">
        <div className="admin-heading-inline">
          <KeyRound size={16} />
          <div>
            <h3 className="auth-settings-title">Recovery Options</h3>
            <p className="admin-muted">Single-use backup codes for account recovery.</p>
          </div>
        </div>
        <div className="admin-setting-list auth-settings-list">
          <div className="admin-setting-item">
            <div>
              <strong>Recovery codes</strong>
              <p className="admin-muted auth-settings-muted-reset">
                Remaining codes: {profile.recoveryOptions.recoveryCodes.remaining} of {profile.recoveryOptions.recoveryCodes.total}
              </p>
            </div>
            <button
              type="button"
              className="admin-button is-primary"
              onClick={() => openSensitiveView("recovery-codes")}
            >
              View / Generate
            </button>
          </div>
        </div>
      </article>
    </div>
  );

  const renderChangePassword = () => (
    <section className="admin-card admin-stack-md auth-settings-panel">
      <div className="admin-row auth-settings-header-row">
        <div className="admin-heading-inline">
          <Lock size={16} />
          <div>
            <h3 className="auth-settings-title">Password</h3>
            <p className="admin-muted">Configured</p>
          </div>
        </div>
        <button
          type="button"
          className="admin-button"
          onClick={() => {
            resetPasswordFlow();
            setView("overview");
          }}
        >
          <ArrowLeft size={16} />
          Back to settings
        </button>
      </div>

      {passwordError ? <div className="admin-alert is-error">{passwordError}</div> : null}
      {passwordMessage ? <div className="admin-alert is-success">{passwordMessage}</div> : null}

      {passwordStep === "form" ? (
        <form className="admin-stack-md" onSubmit={handlePasswordStart}>
          <div className="admin-field">
            <span>Old Password</span>
            <div className="auth-settings-input-row">
              <input
                type={showOldPassword ? "text" : "password"}
                value={passwordForm.oldPassword}
                onChange={(event) => handlePasswordFieldChange("oldPassword", event.target.value)}
                autoComplete="current-password"
                required
                disabled={passwordLoading}
                className="auth-settings-input-grow"
              />
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => setShowOldPassword((prev) => !prev)}
                disabled={passwordLoading}
                aria-label={showOldPassword ? "Hide old password" : "Show old password"}
              >
                {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="admin-field">
            <span>New Password</span>
            <div className="auth-settings-input-row">
              <input
                type={showNewPassword ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={(event) => handlePasswordFieldChange("newPassword", event.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                autoComplete="new-password"
                required
                disabled={passwordLoading}
                className="auth-settings-input-grow"
              />
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                disabled={passwordLoading}
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="admin-field">
            <span>Confirm New Password</span>
            <div className="auth-settings-input-row">
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={passwordForm.confirmNewPassword}
                onChange={(event) => handlePasswordFieldChange("confirmNewPassword", event.target.value)}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                autoComplete="new-password"
                required
                disabled={passwordLoading}
                className="auth-settings-input-grow"
              />
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                disabled={passwordLoading}
                aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {shouldShowPasswordRequirements ? <PasswordRequirements password={requirementsValue} /> : null}

          <p className="admin-muted auth-settings-muted-reset">
            Make sure it&apos;s at least 8 characters with uppercase, lowercase, and a special character.
          </p>

          <div className="admin-row auth-settings-actions-left">
            <button type="submit" className="admin-button" disabled={passwordLoading}>
              {passwordLoading ? <Loader2 size={16} /> : null}
              Update password
            </button>
            <button
              type="button"
              className="admin-button admin-button--ghost"
              onClick={() => navigate("/admin/forgot-password")}
              disabled={passwordLoading}
            >
              I forgot my password
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );

  const renderAuthenticator = () => (
    <section className="admin-card admin-stack-md auth-settings-panel">
      <div className="admin-row auth-settings-header-row">
        <div className="admin-heading-inline">
          <Smartphone size={16} />
          <div>
            <h3 className="auth-settings-title">Authenticator app</h3>
            <p className="admin-muted">Add and verify an app for one-time passcodes.</p>
          </div>
        </div>
        <button
          type="button"
          className="admin-button auth-settings-add-button"
          onClick={() => {
            initializeAuthenticatorSetup();
          }}
          disabled={authenticatorLoading || authenticatorSaving}
        >
          {authenticatorLoading ? <Loader2 size={16} /> : null}
          Add
        </button>
        <button
          type="button"
          className="admin-button"
          onClick={() => setView("overview")}
        >
          <ArrowLeft size={16} />
          Back to settings
        </button>
      </div>

      {authenticatorError ? <div className="admin-alert is-error">{authenticatorError}</div> : null}
      {authenticatorMessage ? <div className="admin-alert is-success">{authenticatorMessage}</div> : null}

      {authenticatorSetupOpen ? (
        <form className="admin-card admin-stack-md" onSubmit={handleAuthenticatorSave}>
          <p className="admin-muted auth-settings-muted-reset">
            Authenticator apps and browser extensions generate one-time passwords used as a second factor during sign-in.
          </p>

          <div>
            <strong>Scan the QR code</strong>
            <p className="admin-muted auth-settings-description-gap">
              Use an authenticator app to scan and connect your account.
            </p>
          </div>

          <div className="admin-card auth-settings-qr-card">
            {authenticatorQrCodeDataUrl ? (
              <img src={authenticatorQrCodeDataUrl} alt="Authenticator setup QR code" className="auth-settings-qr-image" />
            ) : (
              <Smartphone size={48} />
            )}
          </div>

          <p className="admin-muted auth-settings-muted-reset">
            Unable to scan? Use this setup key: <strong>{authenticatorManualKey || "—"}</strong>
          </p>

          <div className="admin-field auth-settings-code-field">
            <span>Verify the code from the app</span>
            <input
              type="text"
              value={authenticatorCode}
              onChange={(event) => {
                const value = event.target.value;
                if (/^\d{0,6}$/.test(value)) {
                  setAuthenticatorCode(value);
                }
              }}
              placeholder="XXXXXX"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
            />
          </div>

          <div className="admin-row auth-settings-actions-tight">
            <button type="submit" className="admin-button is-primary" disabled={authenticatorSaving}>
              {authenticatorSaving ? <Loader2 size={16} /> : null}
              Save
            </button>
            <button
              type="button"
              className="admin-button"
              onClick={() => {
                setAuthenticatorSetupOpen(false);
                setAuthenticatorCode("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="admin-card">
          <div className="admin-row auth-settings-status-row">
            <strong>Status</strong>
            <span className={`admin-chip ${profile.twoFactorAuthentication.authenticatorAppEnabled ? "is-green" : "is-orange"}`}>
              {profile.twoFactorAuthentication.authenticatorAppEnabled ? "Configured" : "Not configured"}
            </span>
          </div>
          <p className="admin-muted auth-settings-muted-reset">
            Click <strong>Add</strong> to begin authenticator app setup.
          </p>
        </div>
      )}
    </section>
  );

  const renderRecoveryCodes = () => (
    <section className="admin-card admin-stack-md auth-settings-panel">
      <div className="admin-row">
        <div className="admin-heading-inline">
          <KeyRound size={16} />
          <div>
            <h3 className="auth-settings-title">Two-factor recovery codes</h3>
            <p className="admin-muted">
              Use recovery codes when you can’t access your authenticator device.
            </p>
          </div>
        </div>
      </div>

      <div className="admin-alert">
        Keep recovery codes updated and stored safely. Regenerating codes invalidates old ones.
      </div>

      <div className="admin-card">
        <h4 className="auth-settings-card-title">Recovery codes</h4>
        <p className="admin-muted auth-settings-description-reset">
          Keep recovery codes as safe as your password. Store them in a password manager or offline secure location.
        </p>

        <div className="admin-row auth-settings-status-row">
          <strong>Recovery code status</strong>
          <span className="admin-chip is-blue">
            Remaining {profile.recoveryOptions.recoveryCodes.remaining} / {profile.recoveryOptions.recoveryCodes.total}
          </span>
        </div>
        <p className="admin-muted auth-settings-description-reset">
          Last generated: {formatDateTime(profile.recoveryOptions.recoveryCodes.generatedAt)}
        </p>

        {recoveryError ? <div className="admin-alert is-error">{recoveryError}</div> : null}
        {recoveryMessage ? <div className="admin-alert is-success">{recoveryMessage}</div> : null}

        {recoveryCodes.length ? (
          <div className="admin-backup-codes auth-settings-recovery-grid">
            {recoveryCodes.map((code, index) => (
              <code key={`${code}-${index}`} className="admin-backup-code auth-settings-recovery-code">{code}</code>
            ))}
          </div>
        ) : null}

        {recoveryCodes.length ? (
          <div className="admin-row auth-settings-actions-tight auth-settings-top-gap">
            <button type="button" className="admin-button" onClick={handleDownloadRecoveryCodes}>
              <Download size={16} />
              Download
            </button>
            <button type="button" className="admin-button" onClick={handlePrintRecoveryCodes}>
              <Printer size={16} />
              Print
            </button>
            <button type="button" className="admin-button" onClick={handleCopyRecoveryCodes}>
              <Copy size={16} />
              Copy
            </button>
          </div>
        ) : null}

        {!recoveryCodes.length ? (
          <div className="admin-alert auth-settings-top-gap">
            Existing recovery codes cannot be revealed again for security reasons. Generate new recovery codes to display them once.
          </div>
        ) : null}
      </div>

      <div className="admin-card">
        <h4 className="auth-settings-card-title">Generate new recovery codes</h4>
        <p className="admin-muted auth-settings-description-reset">
          Generate new codes only when needed. Previous codes stop working immediately.
        </p>
        <div className="admin-row auth-settings-actions-tight">
          <button
            type="button"
            className="admin-button is-primary"
            onClick={handleGenerateRecoveryCodes}
            disabled={recoveryLoading}
          >
            {recoveryLoading ? <Loader2 size={16} /> : null}
            Generate new recovery codes
          </button>
          <button
            type="button"
            className="admin-button"
            onClick={() => setView("overview")}
            disabled={recoveryLoading}
          >
            Back to settings
          </button>
        </div>
      </div>
    </section>
  );

  return (
    <>
      <div className="admin-stack-md auth-settings-page">
        <header className="admin-row">
          <div>
            <h2>Password &amp; Authentication</h2>
            <p className="admin-muted">
              Manage sign-in methods, MFA factors, and account recovery with strict identity verification.
            </p>
          </div>
        </header>

        {profileError ? <div className="admin-alert is-error">{profileError}</div> : null}

        {profileLoading && !profileError ? <div className="admin-alert">Loading authentication settings…</div> : null}

        {!profileLoading ? (
          <>
            {view === "overview" ? renderOverview() : null}
            {view === "change-password" ? renderChangePassword() : null}
            {view === "authenticator" ? renderAuthenticator() : null}
            {view === "recovery-codes" ? renderRecoveryCodes() : null}
          </>
        ) : null}
      </div>

      <SudoModeModal
        open={sudoModalOpen}
        onSuccess={handleSudoSuccess}
        onCancel={handleSudoCancel}
      />

      {passwordOtpModalOpen ? (
        <div className="admin-dialog" role="dialog" aria-modal="true">
          <div className="admin-dialog__backdrop" role="presentation" />
          <div className="admin-dialog__panel">
            <div className="admin-dialog__header">
              <h3>Verify with OTP</h3>
              <button
                type="button"
                className="admin-icon-button"
                onClick={() => {
                  setPasswordOtpModalOpen(false);
                  setPasswordStep("form");
                  setPasswordOtpCode("");
                  setPasswordUseBackupCode(false);
                }}
                disabled={passwordLoading}
              >
                <ArrowLeft size={16} />
              </button>
            </div>

            <form className="admin-dialog__body admin-stack-md" onSubmit={handlePasswordFinish}>
              <p className="admin-muted auth-settings-subtext">
                Enter the code from your authenticator app to complete password update.
              </p>

              <div className="admin-totp-toggle">
                <button
                  type="button"
                  onClick={() => {
                    setPasswordUseBackupCode((prev) => !prev);
                    setPasswordOtpCode("");
                  }}
                  disabled={passwordLoading}
                >
                  {passwordUseBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
                </button>
              </div>

              <div className="admin-field">
                <span>{passwordUseBackupCode ? "Recovery Code" : "Authenticator OTP"}</span>
                <div className="admin-totp-input">
                  <input
                    type="text"
                    value={passwordOtpCode}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (passwordUseBackupCode) {
                        setPasswordOtpCode(value.trim());
                        return;
                      }
                      if (/^\d{0,6}$/.test(value)) {
                        setPasswordOtpCode(value);
                      }
                    }}
                    placeholder={passwordUseBackupCode ? "Enter backup code" : "000000"}
                    maxLength={passwordUseBackupCode ? 64 : 6}
                    autoComplete="one-time-code"
                    inputMode={passwordUseBackupCode ? "text" : "numeric"}
                    disabled={passwordLoading}
                  />
                </div>
                <small className="admin-muted">
                  {passwordUseBackupCode ? "Use one backup code exactly as shown when generated." : "Enter the 6-digit code from your authenticator app."}
                </small>
              </div>

              <div className="admin-dialog__footer">
                <button
                  type="button"
                  className="admin-button"
                  onClick={() => {
                    setPasswordOtpModalOpen(false);
                    setPasswordStep("form");
                    setPasswordOtpCode("");
                    setPasswordUseBackupCode(false);
                  }}
                  disabled={passwordLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-button is-primary" disabled={passwordLoading}>
                  {passwordLoading ? <Loader2 size={16} /> : null}
                  Finalize Password Change
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
